import { useEffect, useMemo } from "react"

import { formatNumber } from "accounting"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import Private from "../../../Components/Private"
import Table from "../../../Components/Table"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { formatCurrency } from "../../../Utils/utils"
import { ANALYTICS_SECTION } from "../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../redux/analytics.slice"
import Section from "./Section"
import SummaryRows from "./SummaryRows/SummaryRows"

const { Text } = Typography

const winRateText = (v) => (v == null ? "-" : `${formatNumber(v, 0, ",", ".")}%`)

// A leaderboard of the actual repeat customers, modelled on Health's Chargeback Olympics: a
// medal for the top three, the customer name + email (Incognito-masked), then their dispute
// count, value at stake, and how often you win against them. Same card + table styling as the
// rest of the page, but it names names instead of drawing yet another bar list.
const columns = (t) => [
  {
    title: t("Customer"),
    dataIndex: "customer_name",
    key: "customer_name",
    render: (_v, r) => (
      <Flex gap={8} align="center" style={{ minWidth: 0 }}>
        {r.sequence_number <= 3 ? (
          <Icon type={`medal${r.sequence_number}`} />
        ) : (
          <Text style={{ display: "inline-block", minWidth: 20, textAlign: "center" }}>
            {r.sequence_number}
          </Text>
        )}
        <Flex vertical style={{ minWidth: 0 }}>
          <Text
            ellipsis={{ tooltip: r.customer_name || r.customer_email }}
            style={{ fontWeight: 500 }}
          >
            <Private>{r.customer_name || r.customer_email}</Private>
          </Text>
          {r.customer_name && (
            <Text
              type="secondary"
              ellipsis={{ tooltip: r.customer_email }}
              style={{ fontSize: 12, fontWeight: 400 }}
            >
              <Private>{r.customer_email}</Private>
            </Text>
          )}
        </Flex>
      </Flex>
    )
  },
  {
    // What they dispute about: the same category vocabulary the donut and the win-rate
    // matrix use, so a name here can be read against those two.
    title: t("Most disputed category"),
    dataIndex: "top_category_label",
    key: "top_category_label",
    render: (v) => (
      <Text type={v ? undefined : "secondary"} ellipsis={{ tooltip: v ? t(v) : "" }}>
        {v ? t(v) : "-"}
      </Text>
    )
  },
  {
    title: t("Last dispute"),
    dataIndex: "last_dispute_at",
    key: "last_dispute_at",
    width: 150,
    render: (v) =>
      v ? (
        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
          {dayjs(v).format("DD MMM YYYY")}
        </Text>
      ) : (
        <Text type="secondary">-</Text>
      )
  },
  {
    title: t("Disputes"),
    dataIndex: "count",
    key: "count",
    width: 96,
    align: "right",
    render: (v) => formatNumber(v)
  },
  {
    // The actionable number: how many of theirs are still in flight right now.
    title: t("Open now"),
    dataIndex: "open_count",
    key: "open_count",
    width: 104,
    align: "right",
    render: (v) => (v ? formatNumber(v) : <Text type="secondary">-</Text>)
  },
  {
    title: t("Value ($)"),
    dataIndex: "amount",
    key: "amount",
    width: 120,
    align: "right",
    render: (v) => <Private>{formatCurrency(v, true)}</Private>
  },
  {
    title: t("Win rate"),
    dataIndex: "win_rate",
    key: "win_rate",
    width: 96,
    align: "right",
    render: winRateText
  }
]

// The summary's cells under the columns above, after the customer column.
export const REPEAT_SUMMARY_CELLS = [
  null,
  null,
  { align: "right", render: (s) => formatNumber(s.disputes_count) },
  null,
  { align: "right", render: (s) => <Private>{formatCurrency(s.amount, true)}</Private> },
  { align: "right", render: (s) => winRateText(s.win_rate) }
]

// How many the card lists; the rest live on the full page behind "View all".
const VISIBLE = 5

const RepeatDisputersPanel = ({ getData }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const repeat = useSelector(selectSectionData(ANALYTICS_SECTION.REPEAT_DISPUTERS))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.REPEAT_DISPUTERS))
  const cols = useMemo(() => columns(t), [t])
  const rows = repeat?.results ?? []

  useEffect(() => {
    getData(ANALYTICS_SECTION.REPEAT_DISPUTERS, { page_size: VISIBLE })
  }, [getData])

  const viewAll = (
    <Button
      type="link"
      onClick={() => navigate(RoutesPaths.recover.analytics_repeat_disputers)}
      icon={<Icon type="chevron_right_small" />}
      iconPosition="end"
      style={{ padding: 0 }}
      size="small"
      text={t("View All")}
    />
  )

  return (
    <Section
      title={t("Repeat disputers")}
      tooltip={t(
        "Customers who have disputed more than once, ranked by number of disputes. See who keeps disputing and whether you win against them."
      )}
      extra={repeat?.count ? viewAll : undefined}
      autoHeight
    >
      {rows.length || fetching ? (
        <Table
          className="chart-table repeat-disputers-table"
          dataSource={rows}
          columns={cols}
          rowKey="customer_email"
          loading={fetching}
          withOutPagination
          // No inner scroll: five rows fit, and the long tail is on the full page.
          scroll={{}}
          tableLayout="fixed"
          showHeader
          summary={() => (
            <SummaryRows
              summary={rows.length ? repeat?.summary : null}
              comparisonSummary={repeat?.comparison_summary}
              comparisonLabel={repeat?.comparison_period?.label}
              cells={REPEAT_SUMMARY_CELLS}
            />
          )}
        />
      ) : (
        <EmptyComponent type="no_data_available" />
      )}
    </Section>
  )
}

export default RepeatDisputersPanel
